// Curated Traditional-Chinese copy for every backend error code outside the
// pair / auth / diary flows.
//
// W39.54. These 68 conditions all had a code and a terse server string, and no
// case in any frontend mapper, so a user saw the server's wording verbatim.
// `backend/tests/test_error_code_i18n_contract.py` had been pinning that gap at
// 67 since W39.2 and failing on any growth — which stops a new code from being
// added quietly, but leaves the existing ones unwritten. This closes it.
//
// The gap mattered because of *where* the server string stops. A representative
// case, `ALREADY_IN_PAIR`: the backend says 「你已在 Pair 中」, which is true and
// ends there; the copy below says 「你已經在配對中了, 請先解除目前的配對再接受新的邀請」 —
// the same fact, plus the thing the user can do about it. That distinction is
// the whole reason these are written rather than mirrored, so it is also the
// standard to hold new ones to.
//
// Shape notes:
//
//   * Grouped by the module that raises the code, which is how somebody looking
//     for "what does the anniversary module say about X" will find it.
//   * `appErrorMessage(code, serverMessage)` prefers the server's message, the
//     same contract the three existing mappers have (`pairErrorMessage`'s
//     docstring says so and `auth.test.ts` pins it). The switch is the
//     *fallback* for when a body carried no `error.message` — a 502 from the
//     reverse proxy during a deploy, which is an HTML page, not the documented
//     envelope.
//   * `appErrorMessage` is the catch-all every page should reach for. The three
//     older mappers stay because their own tests pin their behaviour.
//
// Not here: anything that is a *programming* error rather than a condition a
// person can cause. Those should throw in English and be caught by a boundary.

import { APP_ERROR_I18N } from "./appMessages";

/**
 * The copy for `code`, or `serverMessage` when the server supplied one.
 *
 * The `serverMessage` preference is deliberate and load-bearing: a server that
 * knows more than the client (a validation `details` list, a quota) says it in
 * its own words, and overriding that with a shorter client string loses
 * information. What this function adds is the case where the body was not the
 * documented envelope at all.
 */
export function appErrorMessage(code: string | undefined, serverMessage?: string): string {
  if (serverMessage && serverMessage.trim()) return serverMessage;
  if (!code) return APP_ERROR_I18N.generic;
  switch (code) {
    // --- 相簿 (album) ------------------------------------------------------
    case "ALBUM_NOT_FOUND":
      return APP_ERROR_I18N.album_not_found;
    // W40.15. Split from ALBUM_NOT_FOUND, which used to carry this case. They
    // are different facts — 「this album does not exist」 and 「this album is
    // past its restore window and will be deleted」 — and a person reading the
    // first about a row that is sitting in their trash with a countdown on it is
    // being told something untrue.
    case "ALBUM_RESTORE_WINDOW_EXPIRED":
      return APP_ERROR_I18N.album_restore_window_expired;
    case "ALBUM_FORBIDDEN":
      return APP_ERROR_I18N.album_forbidden;
    case "ALBUM_NAME_TAKEN":
      return APP_ERROR_I18N.album_name_taken;
    case "RESERVED_ALBUM_NAME":
      return APP_ERROR_I18N.album_reserved_name;
    case "SYSTEM_ALBUM_DELETE_FORBIDDEN":
      return APP_ERROR_I18N.album_system_delete;
    case "SYSTEM_ALBUM_RENAME_FORBIDDEN":
      return APP_ERROR_I18N.album_system_rename;
    case "COVER_NOT_IN_ALBUM":
      return APP_ERROR_I18N.album_cover_not_in_album;
    case "ITEM_NOT_FOUND":
      return APP_ERROR_I18N.album_item_not_found;
    case "INVALID_POSITION":
      return APP_ERROR_I18N.album_invalid_position;
    case "FILE_TOO_LARGE":
      return APP_ERROR_I18N.upload_too_large;
    case "MEDIA_TOO_LARGE":
      return APP_ERROR_I18N.upload_too_large;
    case "MAX_FILES_EXCEEDED":
      return APP_ERROR_I18N.upload_too_many;
    case "INVALID_FILE_TYPE":
      return APP_ERROR_I18N.upload_bad_type;
    case "MEDIA_INVALID_TYPE":
      return APP_ERROR_I18N.upload_bad_type;
    case "MEDIA_NOT_FOUND":
      return APP_ERROR_I18N.media_not_found;
    case "MEDIA_FORBIDDEN":
      return APP_ERROR_I18N.media_forbidden;

    // --- 紀念日 (anniversary) ---------------------------------------------
    case "ANNIVERSARY_NOT_FOUND":
      return APP_ERROR_I18N.anniversary_not_found;
    case "ANNIVERSARY_FORBIDDEN":
      return APP_ERROR_I18N.anniversary_forbidden;
    case "ANNIVERSARY_VALIDATION_FAILED":
      return APP_ERROR_I18N.anniversary_validation;
    case "ANNIVERSARY_DATE_OUT_OF_RANGE":
      return APP_ERROR_I18N.anniversary_date_range;
    case "ANNIVERSARY_CUSTOM_LABEL_REQUIRED":
      return APP_ERROR_I18N.anniversary_custom_label_required;
    case "ANNIVERSARY_INVALID_REMINDER_DAYS":
      return APP_ERROR_I18N.anniversary_invalid_reminder;
    case "ANNIVERSARY_MILESTONE_NOT_FOUND":
      return APP_ERROR_I18N.anniversary_milestone_not_found;
    case "ANNIVERSARY_ANCHOR_DATE_LOCKED":
      return APP_ERROR_I18N.anniversary_anchor_date_locked;
    // W39.88. The diary twin of this code has had a case since the Laya SUGGEST
    // work (`diary.ts:124`); the anniversary one did not, and the only two places
    // the client mentioned it were a doc comment on the endpoint and a test
    // fixture that constructs it. `lib/api/api.ts:546` documents it as a real
    // response, so the 503 has somewhere to go.
    //
    // Severity is low and the comment says why: since W39.81 the seven client
    // mappers pass the **server's own message** through when there is one, and
    // every backend message is Chinese. This case is the fallback for when there
    // is no message — a network failure, or a body that is not the envelope.
    case "ANNIVERSARY_FEATURE_TEMPORARILY_UNAVAILABLE":
      return APP_ERROR_I18N.anniversary_feature_unavailable;
    case "ANNIVERSARY_ANCHOR_FIELD_LOCKED":
      return APP_ERROR_I18N.anniversary_anchor_field_locked;
    case "ANNIVERSARY_ANCHOR_RECURRING_LOCKED":
      return APP_ERROR_I18N.anniversary_anchor_recurring_locked;
    case "ANNIVERSARY_ANCHOR_DELETE":
      return APP_ERROR_I18N.anniversary_anchor_delete;
    case "ANNIVERSARY_ANCHOR_MANUAL_CREATE_FORBIDDEN":
      return APP_ERROR_I18N.anniversary_anchor_manual_create_forbidden;
    case "ANNIVERSARY_AUTO_GENERATED_FIELD_LOCKED":
      return APP_ERROR_I18N.anniversary_auto_field_locked;
    case "ANNIVERSARY_AUTO_DELETE":
      return APP_ERROR_I18N.anniversary_auto_delete;
    case "RESTORE_WINDOW_EXPIRED":
      return APP_ERROR_I18N.restore_window_expired;

    // --- 日曆 (calendar) ---------------------------------------------------
    case "CALENDAR_EVENT_NOT_FOUND":
      return APP_ERROR_I18N.calendar_event_not_found;
    case "CALENDAR_FORBIDDEN":
      return APP_ERROR_I18N.calendar_forbidden;
    case "CALENDAR_INVALID_RANGE":
      return APP_ERROR_I18N.calendar_invalid_range;
    case "CALENDAR_LINK_TARGET_NOT_FOUND":
      return APP_ERROR_I18N.calendar_link_target_not_found;
    case "CALENDAR_RANGE_TOO_LONG":
      return APP_ERROR_I18N.calendar_range_too_long;
    case "CALENDAR_RESTORE_WINDOW_EXPIRED":
      return APP_ERROR_I18N.calendar_restore_window;

    // --- 心願 (wish) -------------------------------------------------------
    case "WISH_NOT_FOUND":
      return APP_ERROR_I18N.wish_not_found;
    case "WISH_FORBIDDEN":
      return APP_ERROR_I18N.wish_forbidden;
    case "WISH_PRIVATE":
      return APP_ERROR_I18N.wish_private;
    case "WISH_INVALID_CATEGORY":
      return APP_ERROR_I18N.wish_invalid_category;
    // W53.1 D-5: WISH_ALREADY_COMPLETED is gone from both ends. The backend
    // status machine is free in all 16 directions (ADR-0177), so no code path
    // can raise it — and a mapping for an error that can never arrive is a
    // sentence telling the next reader a rule exists.
    case "WISH_INVALID_COMPLETION_MODE":
      return APP_ERROR_I18N.wish_invalid_completion_mode;
    case "WISH_NOT_YOUR_LOG":
      return APP_ERROR_I18N.wish_not_your_log;
    case "WISH_BLOCK_NOT_FOUND":
      return APP_ERROR_I18N.wish_block_not_found;
    case "WISH_BLOCK_TITLE_REQUIRED":
      return APP_ERROR_I18N.wish_block_title_required;
    case "WISH_REORDER_INCOMPLETE":
      return APP_ERROR_I18N.wish_reorder_incomplete;
    case "WISH_COMPLETION_SOURCES_DUPLICATE":
      return APP_ERROR_I18N.wish_completion_sources_duplicate;
    case "WISH_ALBUM_ITEM_NOT_FOUND":
      return APP_ERROR_I18N.wish_album_item_not_found;
    case "WISH_ALBUM_ITEM_DUPLICATE":
      return APP_ERROR_I18N.wish_album_item_duplicate;
    case "WISH_RESTORE_WINDOW_EXPIRED":
      return APP_ERROR_I18N.wish_restore_window;

    // --- 個人資料 (profile) ------------------------------------------------
    case "PROFILE_NOT_FOUND":
      return APP_ERROR_I18N.profile_not_found;
    case "PROFILE_VALIDATION_FAILED":
      return APP_ERROR_I18N.profile_validation;
    case "PROFILE_PARTNER_FORBIDDEN":
      return APP_ERROR_I18N.profile_partner_forbidden;
    case "PROFILE_BIO_TOO_LONG":
      return APP_ERROR_I18N.profile_bio_too_long;
    case "PROFILE_AVATAR_INVALID":
      return APP_ERROR_I18N.profile_avatar_invalid;
    case "PROFILE_AVATAR_NOT_OWNED":
      return APP_ERROR_I18N.profile_avatar_not_owned;
    case "PROFILE_NON_LEAP_CHOICE_REQUIRED":
      return APP_ERROR_I18N.profile_non_leap_required;
    case "PROFILE_NON_LEAP_CHOICE_NOT_APPLICABLE":
      return APP_ERROR_I18N.profile_non_leap_not_applicable;
    case "PROFILE_NON_LEAP_CHOICE_INVALID":
      return APP_ERROR_I18N.profile_non_leap_invalid;

    // --- 每日留言 / 心情 (daily) -------------------------------------------
    case "DAILY_MESSAGE_NOT_FOUND":
      return APP_ERROR_I18N.daily_message_not_found;
    case "DAILY_MESSAGE_FORBIDDEN":
      return APP_ERROR_I18N.daily_message_forbidden;
    case "DAILY_MESSAGE_DATE_INVALID":
      return APP_ERROR_I18N.daily_message_date_invalid;
    case "MOOD_CHECKIN_INVALID":
      return APP_ERROR_I18N.mood_checkin_invalid;
    case "MOOD_CHECKIN_FORBIDDEN":
      return APP_ERROR_I18N.mood_checkin_forbidden;
    case "MOOD_CHECKIN_DATE_INVALID":
      return APP_ERROR_I18N.mood_checkin_date_invalid;

    // --- 首頁 (dashboard) / 日記 (diary) ------------------------------------
    case "DASHBOARD_NO_PAIR":
      return APP_ERROR_I18N.dashboard_no_pair;
    case "DIARY_LIKE_OWN":
      return APP_ERROR_I18N.diary_like_own;
    case "DIARY_TAG_NOT_FOUND":
      return APP_ERROR_I18N.diary_tag_not_found;
    default:
      return APP_ERROR_I18N.generic;
  }
}
